import { RotateLeft, RotateRight } from '@mui/icons-material';
import { Box, IconButton, Slider, ToggleButton, ToggleButtonGroup, styled } from '@mui/material';
import React, { useCallback, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { EColors, getColor } from 'theme';

const CropContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    height: '100%',
    minHeight: '250px',
    maxHeight: '400px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    flex: '0 1 auto',
    [theme.breakpoints.down('sm')]: {
        minHeight: '200px',
        maxHeight: '300px',
    },
}));

const EditorControls = styled(Box)({
    marginTop: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flexShrink: 0,
});

const SliderContainer = styled(Box)({
    width: '100%',
    padding: '0 8px',
});

const StyledSlider = styled(Slider)({
    color: getColor(EColors.primaryAction),
    '& .MuiSlider-thumb': {
        width: 20,
        height: 20,
    },
    '& .MuiSlider-track': {
        height: 4,
    },
    '& .MuiSlider-rail': {
        height: 4,
    },
});

const RotationControls = styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: '12px',
    marginTop: 0,
});

const RotationButton = styled(IconButton)({
    width: '40px',
    height: '40px',
    backgroundColor: getColor(EColors.v3Background),
    border: `1px solid ${getColor(EColors.v3Border)}`,
    color: getColor(EColors.v3TextPrimary),
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: getColor(EColors.v3ButtonHover),
        borderColor: getColor(EColors.v3BorderSecondary),
    },
    '&:active': {
        backgroundColor: getColor(EColors.v3ButtonPressed),
    },
    '& svg': {
        width: '20px',
        height: '20px',
    },
});

const AspectToggleGroup = styled(ToggleButtonGroup)({
    display: 'flex',
    justifyContent: 'center',
    gap: '4px',
    '& .MuiToggleButtonGroup-grouped': {
        border: `1px solid ${getColor(EColors.v3Border)} !important`,
        borderRadius: '6px !important',
        margin: 0,
    },
});

const AspectToggleButton = styled(ToggleButton)({
    padding: '4px 12px',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: getColor(EColors.v3TextPrimary),
    backgroundColor: getColor(EColors.v3Background),
    textTransform: 'none',
    '&:hover': {
        backgroundColor: getColor(EColors.v3ButtonHover),
    },
    '&.Mui-selected': {
        backgroundColor: getColor(EColors.primaryAction),
        color: '#fff',
        '&:hover': {
            backgroundColor: getColor(EColors.primaryAction),
        },
    },
});

type Point = { x: number; y: number };

const ASPECT_FREE = 'free' as const;
type ToggleAspectValue = number | typeof ASPECT_FREE;

export type AspectOption = { label: string; value: number | undefined };

type ImageEditorProps = {
    image: { url: string; title?: string };
    rotation: number;
    aspect?: number;
    aspectOptions?: AspectOption[];
    backgroundColor?: string;
    setRotation: (rotation: number) => void;
    setCroppedAreaPixels: (pixels: Area | null) => void;
};

export const ImageEditor = ({
    image,
    rotation,
    aspect,
    aspectOptions,
    backgroundColor = getColor(EColors.v3Background),
    setRotation,
    setCroppedAreaPixels,
}: ImageEditorProps) => {
    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState<number>(1);
    const [currentAspect, setCurrentAspect] = useState<number | undefined>(aspect);

    const onCropComplete = useCallback(
        (_croppedAreaPercentages: Area, croppedAreaPixels: Area) => {
            setCroppedAreaPixels(croppedAreaPixels);
        },
        [setCroppedAreaPixels]
    );

    const handleAspectChange = (_: React.MouseEvent<HTMLElement>, val: ToggleAspectValue | null) => {
        if (val !== null) setCurrentAspect(val === ASPECT_FREE ? undefined : val);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%' }}>
            <CropContainer>
                <Cropper
                    image={image?.url}
                    crop={crop}
                    zoom={zoom}
                    rotation={rotation}
                    aspect={currentAspect}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onRotationChange={setRotation}
                    onCropComplete={onCropComplete}
                    minZoom={0.1}
                    restrictPosition={false}
                    style={{ containerStyle: { background: backgroundColor } }}
                />
            </CropContainer>
            <EditorControls>
                {aspectOptions && aspectOptions.length > 1 && (
                    <AspectToggleGroup
                        exclusive
                        value={currentAspect ?? ASPECT_FREE}
                        onChange={handleAspectChange}
                        aria-label="Aspect ratio"
                    >
                        {aspectOptions.map(({ label, value }) => (
                            <AspectToggleButton key={label} value={value ?? ASPECT_FREE} aria-label={label}>
                                {label}
                            </AspectToggleButton>
                        ))}
                    </AspectToggleGroup>
                )}
                <SliderContainer>
                    <StyledSlider
                        value={zoom}
                        min={0.1}
                        max={3}
                        step={0.1}
                        aria-labelledby="Zoom"
                        onChange={(_, zoom) => setZoom(Number(zoom))}
                    />
                </SliderContainer>
                <RotationControls>
                    <RotationButton onClick={() => setRotation(rotation - 90)} aria-label="Rotate counter-clockwise">
                        <RotateLeft />
                    </RotationButton>
                    <RotationButton onClick={() => setRotation(rotation + 90)} aria-label="Rotate clockwise">
                        <RotateRight />
                    </RotationButton>
                </RotationControls>
            </EditorControls>
        </Box>
    );
};
